export enum AssetsSubTypes {
  Asset = 'Asset',
  Credit = 'Credit',
  Liability = 'Liability',
}

export class AssetCheckingSavingModel {
  assetType: number;
  accountNumber?: string;
  accountType?: number;
  balanceOrMarketValue?: number;
  recordStatus?: number;
}

export class AssetHeaderModel {
  appDepositDesc: string;
  cashTowardsPurchaseIncludeInNetWorth: boolean;
  dotId: number;
  earnestAmt: number;
  earnestDesc: string;
  sellerCredit: number;
}

export class OtherAssetModel {
  assetType: number;
  balanceOrMarketValue?: number;
}

export class AssetModel {
  assetId?: number;
  assetType?: number;
  autoOwned?: string;
  borrowerId?: number;
  checkingSavings?: AssetCheckingSavingModel[];
  companyName?: string;
  streetAddress?: string;
  isDeleted: boolean;
  city?: string;
  state?: string;
  zipCode?: string;
  remarks?: string;
  lifeInsurance?: string;
  netWorthBusinessBalance?: string;
  other?: OtherAssetModel[];
  retirementFundsBalance?: string;
  stocksAndBonds?: string;
  totalBalance?: number;
  accountHeldByType?: number;

  static createEmptyModel() {
    const model = new AssetModel();
    model.assetId = 0;
    model.assetType = 0; // shows 'select'
    model.checkingSavings = [];
    model.other = [];
    model.netWorthBusinessBalance = null;
    model.retirementFundsBalance = null;
    model.state = null;
    model.zipCode = null;
    model.isDeleted = false;
    model.streetAddress = null;
    model.city = null;
    model.accountHeldByType = 0;
    return model;
  }
}

export class CreditModel {
  amount?: number;
  associatedLenderCredit?: number;
  associatedLenderCreditId?: number;
  builderName?: string;
  builderNameId?: number;
  creditId?: number;
  creditType?: number;
  description?: string;
  displayOrder?: number;
  maxCreditLimit?: number;
  omitFromOtherAssets?: boolean;
  recordStatus?: number;
  source?: number;
  isDeleted: boolean;
  static createEmptyModel() {
    const model = new CreditModel();
    model.creditId = 0;
    model.creditType = 0; // select
    model.source = 0; // select
    model.isDeleted = false;
    return model;
  }
}

/**
 * ExpenseModel is a type of liability but is stored
 * separate from Liabilities in Byte.
 * Both however show up in the same list.
 * Expense model is to be used for separating it out from
 * liabilities.
 */
export class ExpenseModel {
  accountHeldByType?: number;
  borrowerId?: number;
  expenseId: number;
  expenseType?: number;
  isDeleted: boolean;
  monthlyPayment?: number;
  notes?: string;
  otherDescription?: string;
  paymentsLeft?: number;
  qmatrNotes?: string;
  unpaidBalance?: number;

  static FromLiability(liability: LiabilityModel) {
    const expense = new ExpenseModel();
    expense.accountHeldByType = liability.otherAccountHeldByType;
    expense.borrowerId = liability.borrowerId;
    expense.expenseId = liability.liabilityId;
    expense.expenseType = liability.otherExpenseType;
    expense.isDeleted = liability.isDeleted;
    expense.monthlyPayment = liability.monthlyPayment;
    expense.otherDescription = liability.otherDescription;
    expense.notes = liability.otherNotes;
    expense.paymentsLeft = liability.defaultPaymentLeft;
    expense.qmatrNotes = liability.qmatrNotes;
    expense.unpaidBalance = liability.defaultPaymentLeft * liability.monthlyPayment;
    return expense;
  }

  static ToLiability(expense: ExpenseModel) {
    const liability = new LiabilityModel();
    liability.liabilityType = 2; // Other Liability
    liability.accountType = 9; // Other
    liability.otherAccountHeldByType = expense.accountHeldByType;
    liability.borrowerId = expense.borrowerId;
    liability.liabilityId = expense.expenseId;
    liability.otherExpenseType = expense.expenseType;
    liability.isDeleted = expense.isDeleted;
    liability.monthlyPayment = expense.monthlyPayment;
    liability.otherDescription = expense.otherDescription;
    liability.otherNotes = expense.notes;
    liability.defaultPaymentLeft = expense.paymentsLeft;
    liability.qmatrNotes = expense.qmatrNotes;
    if (liability.monthlyPayment && liability.defaultPaymentLeft) {
      liability.unpaidBalance = liability.monthlyPayment * liability.defaultPaymentLeft;
    }
    return liability;
  }
}

export enum AccountHeldByType {
  NotAssigned = 0,
  Jointly = 1,
  Individually = 2,
}

export class LiabilityModel {
  static createEmptyModel(borrowerId?: number) {
    const model = new LiabilityModel();
    model.borrowerId = borrowerId;
    model.liabilityId = 0; // unsaved new liability
    model.liabilityType = 0; // show 'Select'
    model.accountType = 1; // revolving
    model.helocCreditLimit = null;
    model.notCounted = false;
    model.omitted = false;
    model.toBePaidOff = false;
    model.listedOnCreditReport = false;
    model.resubordinated = false;
    model.mortgageType = 0; // select, verified Clear 1 sets it to 0 on save.

    model.isDeleted = false;
    model.isSelected = false;
    model.moveLeftDisabled = false; // if this is true it means it is a target liability (URLA liability)

    return model;
  }

  accountNumber?: string;
  accountType?: number;
  atrNotes?: string;
  borrowerId?: number;
  city?: string;
  companyName?: string;
  copyLiabilityId?: string;
  defaultPaymentLeft?: number;
  helocCreditLimit?: string;
  isLinkedToReo?: boolean;
  jobExpense1QMATRNotes?: string;
  jobExpense2QMATRNotes?: string;
  jobExpenseAmount1?: string;
  jobExpenseAmount2?: string;
  jobExpenseDesc1?: string;
  jobExpenseDesc2?: string;
  liabilityType?: number;
  liabilityId?: number;
  lienPosition?: number;
  listedOnCreditReport?: boolean;
  monthlyPayment?: number;
  mortgagePayments?: string;
  mortgageType?: number;
  notCounted?: boolean;
  omitted?: boolean;
  otherAccountHeldByType?: AccountHeldByType;
  otherDescription?: string;
  otherExpenseType?: number;
  otherExpenseAmount?: string;
  otherExpenseOwedTo?: string;
  qmatrNotes?: string;
  otherNotes?: string;
  overrideDefault?: boolean;
  overridePaymentLeft?: string;
  recordStatus?: number;
  reoId?: number;
  resubordinated?: boolean;
  state?: string;
  streetAddress?: string;
  toBePaidOff?: boolean;
  unpaidBalance?: number;
  zipCode?: string;

  isDeleted: boolean;
  isSelected: boolean;
  moveLeftDisabled: boolean;
}

export type LiabilityModelExtended = LiabilityModel & { applicationId: number };

export enum ReoAccountHeldByType {
  NotAssigned = 0,
  Jointly = 1,
  Individually = 2,
}

export enum CurrentUsages {
  Investment = 3,
  NotAssigned = 0,
  PrimaryResidence = 1,
  SecondHome = 2,
}

export enum IntendedUsages {
  Investment = 3,
  NotAssigned = 0,
  Other = 4,
  PrimaryResidence = 1,
  SecondHome = 2,
}

export enum RecordStatuses {
  NotAssigned = 0,
  New = 1,
  Update = 2,
  Delete = 3,
}

export enum RealEstateOwnedStatuses {
  NotAssigned = 0,
  PendingSale = 2,
  Rental = 3,
  Retained = 4,
  Sold = 1,
}

export enum RealEstateOwnedTypes {
  CommercialNonResidential = 8,
  Condominium = 2,
  CoOperative = 4,
  Farm = 10,
  HomeAndBusinessCombined = 11,
  Land = 12,
  ManufacturedMobileHome = 7,
  MixedUseResidential = 9,
  MultiFamilyMoreThanFourUnits = 6,
  NotAssigned = 0,
  SingleFamily = 1,
  TownHouse = 3,
  TwoToFourUnitProperty = 5,
}

export class AssetReoModel {
  static DEFAULT_VACANCY_FACTOR = 25.0;
  static createEmptyModel(borrowerId?: number) {
    const model = new AssetReoModel();
    model.accountHeldByType = ReoAccountHeldByType.NotAssigned;
    model.borrowerId = borrowerId;
    model.reoId = 0; // unsaved new reo
    model.isSubjectProperty = false;
    model.isTiIncludedInMortgage = false;
    model.currentOccupancy = 0;
    model.intendedOccupancy = 0;
    model.vacancyFactor = AssetReoModel.DEFAULT_VACANCY_FACTOR; // default is 25%
    model.vacancyFactorOverrideValue = null; // null means it was never set, b/c we need to treat 0 as value
    model.isVacancyFactorOverridden = false;
    model.realEstateOwnedStatus = 0;
    model.realEstateOwnedType = 0;
    model.state = null;
    model.country = 'US';
    model.isCurrentResidence = false;
    model.isLinkedToLiabilities = false;
    model.linkedLiabilityIds = [];
    model.isDeleted = false;
    return model;
  }

  accountHeldByType?: ReoAccountHeldByType;
  borrowerId?: number;
  city?: string;
  country?: string;
  currentOccupancy?: CurrentUsages;
  grossRentalIncome?: number;
  incomeLessVacancy?: number;
  intendedOccupancy?: IntendedUsages;
  isCurrentResidence?: boolean;
  isDeleted: boolean;
  isNetRentalIncomeOverridden: boolean;
  isSubjectProperty?: boolean;
  isTiIncludedInMortgage?: boolean;
  isLinkedToLiabilities?: boolean; // calculated field linkedLiabilityIds.length > 0
  linkedLiabilityIds?: number[];
  marketValue?: number;
  monthlyInsTaxHoa?: number;
  mortgagePayments?: number;
  mortgagesDNADesired?: boolean;
  netRentalIncome?: number;
  netRentalIncomeOverrideValue?: number;
  piti?: number;
  pitiOverrideValue?: number;
  isPitiOverridden: boolean;
  realEstateOwnedStatus?: RealEstateOwnedStatuses;
  realEstateOwnedType?: RealEstateOwnedTypes;
  reoId?: number;
  state?: string;
  street?: string;
  vacancyFactor?: number; // vacancyFactor
  vacancyFactorOverrideValue?: number; // vacancyFactor, when overridden
  isVacancyFactorOverridden: boolean;
  zipCode?: string;
  vaPurchasedOrRefinancedWithVALoan?: boolean;
  vaLoanDate?: string;
  addressAutoCompleteValue?: string;
}

export class AssetLiabilitiesModel {
  applicationId?: number;
  assetHeader: AssetHeaderModel;
  assetsList?: AssetModel[];
  creditsList?: CreditModel[];
  liabilitiesList?: LiabilityModel[];
  expenses: ExpenseModel[];
  realEstateOwnedList?: AssetReoModel[];
  statementCompleted?: number;

  /**
   * Copy constructor to get around an issue with Akita's update
   * entities issue. Akita fails to recognize the updated entity as of same
   * type and entity values are not copied.
   * ref: akita -> updateEntities.js(line 47)
   */
  constructor(copyFrom?: AssetLiabilitiesModel) {
    if (copyFrom) {
      this.applicationId = copyFrom.applicationId;
      this.assetHeader = copyFrom.assetHeader;
      this.assetsList = copyFrom.assetsList;
      this.creditsList = copyFrom.creditsList;
      this.expenses = copyFrom.expenses;
      this.liabilitiesList = copyFrom.liabilitiesList;
      this.realEstateOwnedList = copyFrom.realEstateOwnedList;
      this.statementCompleted = copyFrom.statementCompleted;
    }
  }

  static createEmpty(applicationId: number): AssetLiabilitiesModel {
    const assetLiabilitiesModel = new AssetLiabilitiesModel();
    assetLiabilitiesModel.applicationId = applicationId;
    assetLiabilitiesModel.statementCompleted = 0;
    assetLiabilitiesModel.assetsList = [];
    assetLiabilitiesModel.liabilitiesList = [];
    assetLiabilitiesModel.realEstateOwnedList = [];
    assetLiabilitiesModel.creditsList = [];
    assetLiabilitiesModel.expenses = [];
    assetLiabilitiesModel.assetHeader = new AssetHeaderModel();
    return assetLiabilitiesModel;
  }
}
